import Fuse from "fuse.js";

import { nameSimilarity } from "@/src/lib/similar";
import { validateState } from "@/src/lib/us-states";
import {
  ZIP_CODE_LENGTH,
  first5Digits,
  normalizeAddressForComparison,
  normalizeAddressLine2ForComparison,
} from "@/src/lib/utils/address-normalization";
import { Location } from "@/src/models/location";

// Minimum line1 similarity for a candidate to be considered a street-address match.
const LINE1_MIN_SIMILARITY = 0.6;
// Minimum name similarity required to qualify a candidate when the query has no line1.
const NAME_MIN_SIMILARITY = 0.6;

export type Line2MatchPolicy = "exact" | "ignore";

interface LocationMatchOptions {
  line2Policy?: Line2MatchPolicy;
}

function line2ValuesMatch(
  queryValue: string | null | undefined,
  locationValue: string | null | undefined,
  policy: Line2MatchPolicy
): boolean {
  if (policy === "ignore") return true;

  const qLine2 = normalizeAddressLine2ForComparison(queryValue);
  const lLine2 = normalizeAddressLine2ForComparison(locationValue);

  return qLine2 === lLine2;
}

/**
 * Field-aware equality for the exact-match pass:
 * ZIPs compare on their 5-digit prefix (so "33021" matches "33021-5421")
 * line1 compares with suffix normalization (so "Johnson Street" matches "Johnson St")
 * state compares by abbreviation (so "Florida" matches "FL")
 * everything else is a trimmed/lowercased compare.
 */
function fieldValuesMatch(field: string, queryValue: string, locationValue: string): boolean {
  if (field === "line1") {
    const qAddr = normalizeAddressForComparison(queryValue);
    const lAddr = normalizeAddressForComparison(locationValue);

    return qAddr !== "" && qAddr === lAddr;
  }

  if (field === "state") {
    const qState = validateState(queryValue);
    const lState = validateState(locationValue);

    if (qState && lState) {
      return qState === lState;
    }
  }

  if (field === "postalCode") {
    const qZip = first5Digits(queryValue);
    const lZip = first5Digits(locationValue);

    return qZip !== "" && qZip === lZip;
  }

  return queryValue.trim().toLowerCase() === locationValue.trim().toLowerCase();
}

function exactAddressMatches(
  allLocations: Location[],
  query: Partial<Location>,
  line2Policy: Line2MatchPolicy
): Location[] {
  if (!query.address) return [];

  const addressFields: (keyof NonNullable<typeof query.address>)[] = ["line1", "line2", "city", "state", "postalCode"];

  return allLocations.filter((location) => {
    const locationAddress = location.address;

    if (!locationAddress) return false;

    return addressFields.every((field) => {
      const queryValue = query.address?.[field];
      const locationValue = locationAddress[field];

      if (field === "line2") {
        return line2ValuesMatch(queryValue, locationValue, line2Policy);
      }

      if (!queryValue) return true;

      if (!locationValue) return false;

      return fieldValuesMatch(field, queryValue, locationValue);
    });
  });
}

/**
 * Order locations by how well their name matches the query name, best first.
 * Used to break ties when several locations share the same address (e.g. different
 * departments at one building). No-ops when there's nothing to disambiguate.
 */
function rankByNameSimilarity(locations: Location[], queryName: string | null | undefined): Location[] {
  if (locations.length === 1 || !queryName?.trim()) {
    return locations;
  }

  return [...locations]
    .map((location) => ({
      location,
      nameSim: location.name ? nameSimilarity(queryName, location.name, { allowLengthMismatch: true }) : 0,
    }))
    .sort((a, b) => b.nameSim - a.nameSim)
    .map((scored) => scored.location);
}

// Leading numeric token of a street line (e.g. "910" from "910 N Jefferson St"), or null when absent.
function streetNumber(line1: string | null | undefined): string | null {
  const match = line1?.trim().match(/^(\d+)/);

  return match ? match[1] : null;
}

interface ScoredLocation {
  location: Location;
  score: number;
}

/**
 * Deterministic field-wise scoring for a single candidate against a structured query.
 * Returns null when the candidate does not clear the qualification bar for the
 * strongest signal the query provides (line1, else ZIP, else city+state).
 */
function scoreLocation(
  query: Partial<Location>,
  location: Location,
  line2Policy: Line2MatchPolicy
): ScoredLocation | null {
  const qAddr = query.address;
  const lAddr = location.address;

  const qName = query.name?.trim() || "";
  const qLine1 = qAddr?.line1?.trim() || "";
  const qCity = qAddr?.city?.trim() || "";
  const qState = qAddr?.state?.trim() || "";
  const qZip5 = first5Digits(qAddr?.postalCode);

  const lName = location.name?.trim() || "";
  const lLine1 = lAddr?.line1?.trim() || "";
  const lCity = lAddr?.city?.trim() || "";
  const lState = lAddr?.state?.trim() || "";
  const lZip5 = first5Digits(lAddr?.postalCode);

  const hasLine1 = qLine1 !== "";
  const hasName = !!query.name?.trim();
  const line2Agrees = line2ValuesMatch(qAddr?.line2, lAddr?.line2, line2Policy);

  // line1 similarity + street-number agreement.
  const line1Sim = hasLine1
    ? nameSimilarity(normalizeAddressForComparison(qLine1), normalizeAddressForComparison(lLine1), {
        allowLengthMismatch: true,
      })
    : 0;
  const qStreetNum = streetNumber(qLine1);
  const lStreetNum = streetNumber(lLine1);
  const bothHaveStreetNum = qStreetNum !== null && lStreetNum !== null;
  const streetNumberMatch = bothHaveStreetNum && qStreetNum === lStreetNum;
  const line1Agrees =
    hasLine1 &&
    (bothHaveStreetNum ? streetNumberMatch && line1Sim >= LINE1_MIN_SIMILARITY : line1Sim >= LINE1_MIN_SIMILARITY);

  // Geo: city, state, and ZIP all answer "is this the same locality?" — treat them as one signal.
  // A single agreement confirms it (ZIP, or city+state); we only look for positive confirmation.
  const zipMatch = lZip5.length === ZIP_CODE_LENGTH && qZip5 === lZip5;
  const cityMatch = qCity !== "" && lCity !== "" && qCity.toLowerCase() === lCity.toLowerCase();
  const qStateAbbr = validateState(qState);
  const lStateAbbr = validateState(lState);
  const stateMatch = qStateAbbr !== null && lStateAbbr !== null && qStateAbbr === lStateAbbr;
  const geoConfirmed = zipMatch || (cityMatch && stateMatch);

  const nameSim = qName && lName ? nameSimilarity(qName, lName, { allowLengthMismatch: true }) : 0;

  // Qualification: a street match positively confirmed by geo, or — when there's no street
  // line — a name match positively confirmed by geo. Geo confirmation is required either way.
  let qualifies: boolean;

  if (hasLine1) {
    qualifies = line1Agrees && line2Agrees && geoConfirmed;
  } else if (hasName) {
    qualifies = nameSim >= NAME_MIN_SIMILARITY && line2Agrees && geoConfirmed;
  } else {
    qualifies = false;
  }

  if (!qualifies) return null;

  // Ranking among qualified candidates: dominated by line1, with name as the tiebreak.
  // Geo is omitted — it's already required to qualify, so it's constant here and can't rank.
  const score = line1Sim * 5 + nameSim;

  return { location, score };
}

/**
 * Match a structured (extracted) address against existing locations.
 *
 * @returns Matching locations ordered best-first.
 */
export function matchLocationByAddress(
  allLocations: Location[],
  query: Partial<Location> | null,
  options: LocationMatchOptions = {}
): Location[] {
  if (!allLocations || allLocations.length === 0 || !query) {
    return [];
  }

  const line2Policy = options.line2Policy ?? "ignore";
  const exactMatches = exactAddressMatches(allLocations, query, line2Policy);

  if (exactMatches.length > 0) {
    // Several departments can share one address; rank by name so the right one wins.
    return rankByNameSimilarity(exactMatches, query.name);
  }

  return allLocations
    .map((location) => scoreLocation(query, location, line2Policy))
    .filter((scored): scored is ScoredLocation => scored !== null)
    .sort((a, b) => b.score - a.score)
    .map((scored) => scored.location);
}

/**
 * Free-text typeahead search over locations (name + address fields).
 *
 * @returns Up to 20 fuzzy matches ordered by relevance.
 */
export function searchLocationsByText(allLocations: Location[], query: string): Location[] {
  if (!allLocations || allLocations.length === 0) {
    return [];
  }

  const searchPattern = query.trim();

  if (!searchPattern) {
    return [];
  }

  const fuse = new Fuse(allLocations, {
    keys: [
      { name: "name", weight: 3 },
      { name: "address.line1", weight: 3 },
      { name: "address.line2", weight: 2 },
      { name: "address.city", weight: 2 },
      { name: "address.state", weight: 1 },
      { name: "address.postalCode", weight: 1 },
    ],
    threshold: 0.5,
    ignoreLocation: true,
    isCaseSensitive: false,
    includeMatches: true,
    minMatchCharLength: 2,
  });

  const results = fuse.search(searchPattern, { limit: 20 });

  return results.map((result) => result.item);
}
